import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {useLayout, usePageData} from '../../../_metronic/layout/core'
import {KTCard, KTCardBody, KTIcon, useDebounce} from '../../../_metronic/helpers'
import {
  ExpandedState,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import toast from 'react-hot-toast'
import {CardsWidget7} from '../../../_metronic/partials/widgets'
import {SAMPLE as SAMPLE_DATA} from './sampleResponse'
import {getBalanceSheetData} from './api'
import * as XLSX from 'xlsx'
import {Loader} from '../product-master/loader'
import {getBalanceSheetColumns, applyHeaderStyles} from './columns'
import {
  transformAssetsData,
  transformEquityData,
  transformLiabilitiesData,
  formatCurrency,
} from './utils'
import {IBalanceSheetRow} from './types'
import {useNavigate} from 'react-router-dom'
import {error} from 'console'

export const BalanceSheet = () => {
  const {setPageTitle} = usePageData()
  const {company, financialYear, fyEndDate} = useLayout()
  const navigate = useNavigate()
  useEffect(() => {
    if (!company || !company.value || !financialYear || !financialYear.value) {
      navigate('/companies')
    }
  }, [company.value, financialYear.value])

  useEffect(() => {
    setPageTitle('Balance Sheet')
    // Apply the header styles for merged headers
    applyHeaderStyles()
  }, [setPageTitle])

  const [assetsData, setAssetsData] = useState<IBalanceSheetRow[]>([])
  const [liabilitiesData, setLiabilitiesData] = useState<IBalanceSheetRow[]>([])
  const [equityData, setEquityData] = useState<IBalanceSheetRow[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isError, setIsError] = useState(false)

  const [to, setTo] = useState<string>('')
  const [isDateUpdate, setIsDateUpdate] = useState(false)
  const [totals, setTotals] = useState({
    totalAssets: '',
    totalLiabilities: '',
    totalEquity: '',
    totalLiabilitiesAndEquity: '',
    difference: '',
  })
  const [isButtonHovered, setIsButtonHovered] = useState(false)

  const getData = useCallback(async () => {
    if (company.value && to && financialYear.value && isDateUpdate) {
      try {
        setIsError(false)
        setIsLoading(true)

        // For development/testing, use SAMPLE_DATA
        // In production, use the actual API call below
        const res: any = await getBalanceSheetData(
          Number(company.value),
          financialYear.value,
          to || ''
        )

        // const res = SAMPLE_DATA
        if (!res) throw new Error('Internal Server Error')
        if (res && res.message) throw new Error(res.message)

        if (res && res.status !== 'error') {
          // console.log(res)
          // Transform the data for the tables
          const assets = transformAssetsData(res.assets)
          const liabilities = transformLiabilitiesData(res.liability)
          const equity = transformEquityData(res.liability)

          setAssetsData(assets)
          setLiabilitiesData(liabilities)
          setEquityData(equity)

          // Set the totals
          setTotals({
            totalAssets: formatCurrency(res.assets.total),
            totalLiabilities: formatCurrency(res.liability.total - res.liability.equity.value),
            totalEquity: formatCurrency(res.liability.equity.value),
            totalLiabilitiesAndEquity: formatCurrency(res.liability.total),
            difference: formatCurrency(res.assets.total - res.liability.total),
          })
        }
      } catch (err: any) {
        console.log(err)
        setIsError(true)
        // console.error('Error fetching balance sheet data:', err)
        toast.error(err.message)
        setAssetsData([])
        setEquityData([])
        setLiabilitiesData([])
        setTotals({
          totalAssets: '',
          totalLiabilities: '',
          totalEquity: '',
          totalLiabilitiesAndEquity: '',
          difference: '',
        })
      } finally {
        setIsLoading(false)
        setIsDateUpdate(false)
      }
    }
  }, [company.value, financialYear.value, to, isDateUpdate])

  useEffect(() => {
    getData()
  }, [getData])

  useEffect(() => {
    // Set the initial date range based on financial year
    if (fyEndDate) {
      setTo(fyEndDate)
    } else {
      const storedToDate = localStorage.getItem('toDate')
      if (!storedToDate) {
        if (!financialYear || !financialYear?.value) return
        const [year1, year2] = financialYear.value.split('-')

        setTo(new Date(Number(year2.trim()), 3, 1).toISOString().split('T')[0])
      } else {
        setTo(storedToDate)
      }
    }
    setIsDateUpdate(true)
  }, [fyEndDate])

  useEffect(() => {
    if (isDateUpdate) {
      localStorage.setItem('toDate', to)
    }
  }, [isDateUpdate])

  const columns = useMemo(() => getBalanceSheetColumns(to), [to])

  // State for expanded rows in each table
  const [assetsExpanded, setAssetsExpanded] = useState<ExpandedState>({})
  const [liabilitiesExpanded, setLiabilitiesExpanded] = useState<ExpandedState>({})
  const [equityExpanded, setEquityExpanded] = useState<ExpandedState>({})

  // Create tables for each section
  const assetsTable = useReactTable({
    columns,
    data: assetsData,
    state: {
      expanded: assetsExpanded,
    },
    onExpandedChange: setAssetsExpanded,
    getSubRows: (row) => row.children,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getRowCanExpand: (row) => !!row.original.children?.length,
  })

  const liabilitiesTable = useReactTable({
    columns,
    data: liabilitiesData,
    state: {
      expanded: liabilitiesExpanded,
    },
    onExpandedChange: setLiabilitiesExpanded,
    getSubRows: (row) => row.children,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getRowCanExpand: (row) => !!row.original.children?.length,
  })

  const equityTable = useReactTable({
    columns,
    data: equityData,
    state: {
      expanded: equityExpanded,
    },
    onExpandedChange: setEquityExpanded,
    getSubRows: (row) => row.children,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getRowCanExpand: (row) => !!row.original.children?.length,
  })

  // References for the tables for export
  const assetsTableRef = useRef<HTMLTableElement>(null)
  const liabilitiesTableRef = useRef<HTMLTableElement>(null)
  const equityTableRef = useRef<HTMLTableElement>(null)

  // Function to export the tables to Excel
  const handleDownload = () => {
    toast.success('Download has been started successfully')

    // Get data from tables
    const assetTable = assetsTableRef.current
    const liabilityTable = liabilitiesTableRef.current
    const equityTable = equityTableRef.current

    if (!assetTable || !liabilityTable || !equityTable) {
      toast.error('Tables are not ready for export')
      return
    }
    // Create worksheets for each table
    const ws1 = XLSX.utils.table_to_sheet(assetTable)
    const ws2 = XLSX.utils.table_to_sheet(liabilityTable)
    const ws3 = XLSX.utils.table_to_sheet(equityTable)

    // Create a new workbook and append the worksheets
    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, ws1, 'Assets')
    XLSX.utils.book_append_sheet(workbook, ws2, 'Liabilities')
    XLSX.utils.book_append_sheet(workbook, ws3, 'Equity')

    // Generate filename with company name and financial year
    const fileName = `BalanceSheet_${company.label}_${financialYear.value}.xlsx`

    // Write and download the Excel file
    XLSX.writeFile(workbook, fileName)
  }

  // Table section component for reuse
  const TableSection = ({
    title,
    table,
    tableRef,
  }: {
    title: string
    table: any
    tableRef: React.RefObject<HTMLTableElement>
  }) => {
    // // console.log(table.getHeaderGroups())
    return (
      <div className='mb-10'>
        {/* <h3 className='fs-4 fw-bold mb-3'>{title}</h3> */}
        <div className='table-responsive'>
          <table
            className='table align-middle fs-6 dataTable'
            ref={tableRef}
            style={{
              tableLayout: 'fixed',
            }}
          >
            {title === 'Assets' && (
              <thead>
                {table.getHeaderGroups().map((headerGroup: any) => (
                  <tr
                    key={headerGroup.id}
                    className='text-start text-muted fw-bolder bg-light ps-4 fs-7 text-uppercase gs-0'
                  >
                    {headerGroup.headers.map((header: any) => (
                      <th
                        key={header.index}
                        colSpan={header.colSpan}
                        className='min-w-125px'
                        style={{
                          border: '1px solid #e1e3ea',
                        }}
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(header.column.columnDef.header, header.getContext())}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
            )}
            <tbody className='text-gray-600 fw-bold'>
              {table.getRowModel().rows.length > 0 ? (
                table.getRowModel().rows.map((row: any) => (
                  <tr
                    key={row.id}
                    onClick={row.getCanExpand() ? () => row.toggleExpanded() : undefined}
                    className={
                      row.getCanExpand() ? 'cursor-pointer bg-hover-light' : 'bg-hover-light'
                    }
                  >
                    {row.getVisibleCells().map((cell: any) => (
                      <td
                        key={cell.id}
                        style={{
                          paddingLeft: row.depth > 0 ? `${row.depth * 1.5 + 0.5}rem` : '0.5rem',
                          // paddingLeft: '0.5rem',
                          // border: '1px solid #e1e3ea',
                        }}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={table.getAllColumns().length} className='text-center py-4'>
                    No data available
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    )
  }

  return (
    <>
      <KTCard className='mb-5'>
        <div className='card-header border-0'>
          <div className='card-title'>
            {/* Date range selection */}
            <div className='d-flex align-items-center position-relative my-1'>
              <input
                type='date'
                className='form-control form-control-solid w-250px ps-14'
                placeholder='To Date'
                value={to}
                onChange={(e) => setTo(e.target.value)}
              />
              <button
                type='button'
                className='btn btn-lg ms-3'
                style={{
                  borderRadius: '1000%',
                  height: '30px',
                  width: '30px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: 0,
                  backgroundColor: !isButtonHovered ? '#f0f0f0' : '#b0b0b0',
                }}
                onMouseEnter={() => setIsButtonHovered(true)}
                onMouseLeave={() => setIsButtonHovered(false)}
                onClick={() => {
                  if (!to) {
                    toast.error('Please make sure both the From and To dates are selected')
                  } else {
                    setIsDateUpdate(true)
                  }
                }}
              >
                <KTIcon iconName='arrows-circle' className='fs-3 px-0' />
              </button>
            </div>
          </div>

          {/* Card toolbar - Export button */}
          <div className='card-toolbar'>
            <div className='d-flex justify-content-end' data-kt-user-table-toolbar='base'>
              <button
                disabled={isLoading || !assetsData.length}
                type='button'
                className='btn p-0'
                style={{
                  backgroundColor: 'var(--bs-primary)',
                  color: 'var(--bs-primary-inverse)',
                  display: 'inline-flex',
                  height: '43.59px',
                  alignItems: 'center',
                  justifyContent: 'space-between',

                  boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                }}
                onClick={handleDownload}
              >
                <span className='px-5'>Download</span>
                <span
                  style={{
                    color: '#fff',
                    backgroundColor: '#006060',
                    padding: '0 15px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                    borderTopRightRadius: 'inherit',
                    borderBottomRightRadius: 'inherit',
                  }}
                >
                  <KTIcon iconName='arrow-down' className='fs-2 text-white' />
                </span>
              </button>
            </div>
          </div>
        </div>
      </KTCard>
      <KTCard>
        <KTCardBody className={`py-4 ${to && isDateUpdate ? 'position-relative' : ''}`}>
          {isLoading ? (
            <Loader isError={isError} />
          ) : (
            <>
              {/* Assets Table */}
              <div className='d-flex justify-content-start mb-4'>
                <div className='btn-group'>
                  <button
                    type='button'
                    className='btn btn-sm btn-light-primary'
                    onClick={() => {
                      assetsTable.toggleAllRowsExpanded(true)
                      liabilitiesTable.toggleAllRowsExpanded(true)
                      equityTable.toggleAllRowsExpanded(true)
                    }}
                    title='Expand All'
                  >
                    <KTIcon iconName='double-down' className='fs-3 me-1' />
                    Expand All
                  </button>
                  <button
                    type='button'
                    className='btn btn-sm btn-light-primary'
                    onClick={() => {
                      assetsTable.toggleAllRowsExpanded(false)
                      liabilitiesTable.toggleAllRowsExpanded(false)
                      equityTable.toggleAllRowsExpanded(false)
                    }}
                    title='Collapse All'
                  >
                    <KTIcon iconName='double-up' className='fs-3 me-1' />
                    Collapse All
                  </button>
                </div>
              </div>
              <TableSection title='Assets' table={assetsTable} tableRef={assetsTableRef} />

              {/* Total Assets Card */}
              <div className='d-flex justify-content-end mb-10'>
                <CardsWidget7
                  className='border border-dashed border-gray-300'
                  stats={totals.totalAssets}
                  description='Total Assets'
                />
              </div>

              {/* Liabilities Table */}
              <TableSection
                title='Liabilities'
                table={liabilitiesTable}
                tableRef={liabilitiesTableRef}
              />

              {/* Total Liabilities Card */}
              <div className='d-flex justify-content-end mb-10'>
                <CardsWidget7
                  className='border border-dashed border-gray-300'
                  stats={totals.totalLiabilities}
                  description='Total Liabilities'
                />
              </div>

              {/* Equity Table */}
              <TableSection title='Equity' table={equityTable} tableRef={equityTableRef} />

              {/* Total Equity and Combined Total Cards */}
              <div className='d-flex flex-column align-items-end flex-wrap gap-3'>
                {/* <CardsWidget7
                  className='border border-dashed border-gray-300'
                  stats={totals.totalEquity}
                  description='Total Equity'
                /> */}
                <CardsWidget7
                  className='border border-dashed border-gray-300'
                  stats={totals.totalLiabilitiesAndEquity}
                  description='Total Liabilities & Equity'
                />

                <CardsWidget7
                  className='border border-dashed border-gray-300'
                  stats={totals.difference}
                  description='Difference'
                />
              </div>
            </>
          )}
        </KTCardBody>
      </KTCard>
    </>
  )
}
